import { Routes } from '@angular/router';
import { backOfficeAuthGuard } from './backOffice/guards/back-office-auth.guard';
import { kybStepGuard } from './onboarding/kyb/guards/kyb-step.guard';
import { kycStepGuard } from './onboarding/kyc/guards/kyc-step.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./onboarding/main/start-onboarding/start-onboarding.component').then(m => m.StartOnboardingComponent),
  },
  {
    path: 'products',
    loadComponent: () =>
      import('./onboarding/main/products/products.component').then(m => m.ProductsComponent),
  },

  // Recuperación de flujo: aterrizaje de los magic links que envía el backend.
  {
    path: 'resume',
    loadComponent: () => import('./onboarding/main/resume/resume.component').then(m => m.ResumeComponent),
  },

  // KYC - Persona física (Cards)
  {
    path: 'cards/kyc',
    loadComponent: () => import('./onboarding/kyc/pages/main/main.component').then(m => m.KycComponent),
    canActivate: [kycStepGuard('CONTACT_INFO')],
  },
  {
    path: 'cards/kyc/company-data',
    loadComponent: () => import('./onboarding/kyc/pages/company-data/company-data.component').then(m => m.CompanyDataComponent),
    canActivate: [kycStepGuard('LABOR_INFO')],
  },
  {
    path: 'cards/kyc/financial-profile',
    loadComponent: () => import('./onboarding/kyc/pages/financial-profile/financial-profile.component').then(m => m.FinancialProfileComponent),
    canActivate: [kycStepGuard('FINANCIAL_PROFILE')],
  },
  // 2026-05-22: guards alineados con KYC_ROUTES tras agregar IDENTITY_VERIFICATION
  // y FORM_INFO al stepsArray (ver kyc-flow-state.service). Antes /documentation y
  // /representative-data exigían DOCUMENTS/REVIEW respectivamente: al avanzar de
  // FINANCIAL_PROFILE a IDENTITY_VERIFICATION el router caía en un loop de redirect
  // (currentStep < target → guard redirige a currentRoute = misma URL) y el spinner
  // se quedaba pegado en perfil financiero.
  {
    path: 'cards/kyc/documentation',
    loadComponent: () => import('./onboarding/kyc/pages/documentation/documentation.component').then(m => m.DocumentationComponent),
    canActivate: [kycStepGuard('IDENTITY_VERIFICATION')],
  },
  {
    path: 'cards/kyc/representative-data',
    loadComponent: () => import('./onboarding/kyc/pages/representative-data/representative-data.component').then(m => m.RepresentativeDataComponent),
    canActivate: [kycStepGuard('DOCUMENTS')],
  },
  {
    path: 'cards/kyc/applicant-form',
    loadComponent: () => import('./onboarding/kyc/pages/applicant-form/applicant-form.component').then(m => m.ApplicantFormComponent),
    canActivate: [kycStepGuard('FORM_INFO')],
  },
  {
    path: 'cards/kyc/final-registration',
    loadComponent: () => import('./onboarding/kyc/pages/final-registration/final-registration.component').then(m => m.FinalRegistrationComponent),
    canActivate: [kycStepGuard('REVIEW')],
  },
  {
    path: 'cards/kyc/request-sent',
    loadComponent: () => import('./onboarding/kyc/pages/request-sent/request-sent.component').then(m => m.RequestSentComponent),
  },

  // KYB PFAE — Persona Física con Actividad Empresarial
  {
    path: 'kyb-fisic/contact-info',
    loadComponent: () => import('./onboarding/kyb/pages/contact-info/contact-info.component').then(m => m.ContactInfoComponent),
    canActivate: [kybStepGuard('CONTACT_INFO')],
  },
  {
    path: 'kyb-fisic/visualization-sat',
    loadComponent: () => import('./onboarding/kyb/pages/visualization-sat/visualization-sat.component').then(m => m.VisualizationSATComponent),
    canActivate: [kybStepGuard('SAT_INFO')],
  },
  {
    path: 'kyb-fisic/legal-representative',
    loadComponent: () => import('./onboarding/kyb/pages/legal-representative/legal-representative.component').then(m => m.LegalRepresentativeComponent),
    canActivate: [kybStepGuard('LEGAL_REP')],
  },
  {
    path: 'kyb-fisic/documentation',
    loadComponent: () => import('./onboarding/kyb/pages/documentation/documentation.component').then(m => m.DocumentationComponent),
    canActivate: [kybStepGuard('DOCUMENTS')],
  },
  // 2026-05-19 spec mayo 2026: applicant-form ahora es el step backend FORM_INFO
  // (antes UI-only protegido por kybStepGuard('REVIEW')). Hace POST /solicitant-form.
  {
    path: 'kyb-fisic/applicant-form',
    loadComponent: () => import('./onboarding/kyb/pages/applicant-form/applicant-form.component').then(m => m.ApplicantFormComponent),
    canActivate: [kybStepGuard('FORM_INFO')],
  },
  {
    path: 'kyb-fisic/final-registration',
    loadComponent: () => import('./onboarding/kyb/pages/final-registration/final-registration.component').then(m => m.KybFinalRegistrationComponent),
    canActivate: [kybStepGuard('REVIEW')],
  },

  // POS PFAE — mismas pantallas que KYB PFAE (Suite). El producto se setea
  // en sessionStorage por ProductsComponent antes de POST /start y dispara
  // las variantes de stepper/copies en los componentes (sin paso UI-only
  // "Formulario del solicitante") y oculta secciones extras de Documentación.
  {
    path: 'pos-fisic/contact-info',
    loadComponent: () => import('./onboarding/kyb/pages/contact-info/contact-info.component').then(m => m.ContactInfoComponent),
    canActivate: [kybStepGuard('CONTACT_INFO')],
  },
  {
    path: 'pos-fisic/visualization-sat',
    loadComponent: () => import('./onboarding/kyb/pages/visualization-sat/visualization-sat.component').then(m => m.VisualizationSATComponent),
    canActivate: [kybStepGuard('SAT_INFO')],
  },
  {
    path: 'pos-fisic/legal-representative',
    loadComponent: () => import('./onboarding/kyb/pages/legal-representative/legal-representative.component').then(m => m.LegalRepresentativeComponent),
    canActivate: [kybStepGuard('LEGAL_REP')],
  },
  {
    path: 'pos-fisic/documentation',
    loadComponent: () => import('./onboarding/kyb/pages/documentation/documentation.component').then(m => m.DocumentationComponent),
    canActivate: [kybStepGuard('DOCUMENTS')],
  },
  {
    path: 'pos-fisic/final-registration',
    loadComponent: () => import('./onboarding/kyb/pages/final-registration/final-registration.component').then(m => m.KybFinalRegistrationComponent),
    canActivate: [kybStepGuard('REVIEW')],
  },

  // KYB PM — Persona Moral
  {
    path: 'kyb-pm/contact-info',
    loadComponent: () => import('./onboarding/kyb-pm/pages/contact-info/contact-info.component').then(m => m.ContactInfoComponent),
    canActivate: [kybStepGuard('CONTACT_INFO')],
  },
  /* {
    path: 'kyb-pm/applicant-form',
    loadComponent: () => import('./onboarding/kyb-pm/pages/applicant-form/applicant-form.component').then(m => m.KybApplicantFormComponent),
  }, */
  {
    path: 'kyb-pm/company-data',
    loadComponent: () => import('./onboarding/kyb-pm/pages/company-data/company-data.component').then(m => m.CompanyDataComponent),
    canActivate: [kybStepGuard('COMPANY_INFO')],
  },
  {
    path: 'kyb-pm/visualization-sat',
    loadComponent: () => import('./onboarding/kyb-pm/pages/visualization-sat/visualization-sat.component').then(m => m.VisualizationSATComponent),
    canActivate: [kybStepGuard('SAT_INFO')],
  },
  // DEPRECATED: pantalla `identity-verification` fuera del flujo SUITE PM
  // (no está en el stepper de buildLabels). El componente se conserva por si
  // se reactiva en el futuro.
  // {
  //   path: 'kyb-pm/identity-verification',
  //   loadComponent: () => import('./onboarding/kyb-pm/pages/identity-verification/identity-verification.component').then(m => m.KybIdentityVerificationComponent),
  // },
  {
    path: 'kyb-pm/legal-representative',
    loadComponent: () => import('./onboarding/kyb-pm/pages/legal-representative/legal-representative.component').then(m => m.LegalRepresentativeComponent),
    canActivate: [kybStepGuard('LEGAL_REP')],
  },
  {
    path: 'kyb-pm/documentation',
    loadComponent: () => import('./onboarding/kyb-pm/pages/documentation/documentation.component').then(m => m.DocumentationComponent),
    canActivate: [kybStepGuard('DOCUMENTS')],
  },
  // DEPRECATED 2026-04-28: pantalla de Accionistas eliminada del flujo PM.
  // Componente y guard se conservan por si la sección regresa.
  // {
  //   path: 'kyb-pm/shareholders',
  //   loadComponent: () => import('./onboarding/kyb-pm/pages/shareholders/shareholders.component').then(m => m.ShareholdersComponent),
  //   canActivate: [kybStepGuard('SHAREHOLDERS')],
  // },
  // 2026-05-19 spec mayo 2026: applicant-form PM ahora es el step backend FORM_INFO
  // (antes UI-only protegido por kybStepGuard('REVIEW')). Mismo patrón que PFAE.
  {
    path: 'kyb-pm/applicant-form',
    loadComponent: () => import('./onboarding/kyb-pm/pages/applicant-form/applicant-form.component').then(m => m.KybApplicantFormComponent),
    canActivate: [kybStepGuard('FORM_INFO')],
  },
  {
    path: 'kyb-pm/final-registration',
    loadComponent: () => import('./onboarding/kyb-pm/pages/final-registration/final-registration.component').then(m => m.KybFinalRegistrationComponent),
    canActivate: [kybStepGuard('REVIEW')],
  },

  // POS PM — mismas pantallas que KYB PM (Suite). El producto se setea en
  // sessionStorage por ProductsComponent antes de POST /start y dispara las
  // variantes POS en KybFlowStateService (stepper sin "Formulario del
  // solicitante" UI-only; rutas /pos-pm/* en getRouteForStep). Documentación
  // oculta <app-pm-document-extras> cuando producto=POS.
  {
    path: 'pos-pm/contact-info',
    loadComponent: () => import('./onboarding/kyb-pm/pages/contact-info/contact-info.component').then(m => m.ContactInfoComponent),
    canActivate: [kybStepGuard('CONTACT_INFO')],
  },
  {
    path: 'pos-pm/company-data',
    loadComponent: () => import('./onboarding/kyb-pm/pages/company-data/company-data.component').then(m => m.CompanyDataComponent),
    canActivate: [kybStepGuard('COMPANY_INFO')],
  },
  {
    path: 'pos-pm/visualization-sat',
    loadComponent: () => import('./onboarding/kyb-pm/pages/visualization-sat/visualization-sat.component').then(m => m.VisualizationSATComponent),
    canActivate: [kybStepGuard('SAT_INFO')],
  },
  {
    path: 'pos-pm/legal-representative',
    loadComponent: () => import('./onboarding/kyb-pm/pages/legal-representative/legal-representative.component').then(m => m.LegalRepresentativeComponent),
    canActivate: [kybStepGuard('LEGAL_REP')],
  },
  {
    path: 'pos-pm/documentation',
    loadComponent: () => import('./onboarding/kyb-pm/pages/documentation/documentation.component').then(m => m.DocumentationComponent),
    canActivate: [kybStepGuard('DOCUMENTS')],
  },
  {
    path: 'pos-pm/final-registration',
    loadComponent: () => import('./onboarding/kyb-pm/pages/final-registration/final-registration.component').then(m => m.KybFinalRegistrationComponent),
    canActivate: [kybStepGuard('REVIEW')],
  },

  // /cards -> redirige a productos (RequestSentComponent navega aquí)
  { path: 'cards', redirectTo: 'products', pathMatch: 'full' },

  // Back Office — login admin
  {
    path: 'internal/ops/v2/review/compliance/login',
    loadComponent: () => import('./backOffice/login/back-office-login.component').then(m => m.BackOfficeLoginComponent),
  },
  {
    path: 'internal/ops/v2/review/compliance/verify-2fa',
    loadComponent: () => import('./backOffice/verify-2fa/back-office-verify-2fa.component').then(m => m.BackOfficeVerify2faComponent),
  },
  {
    path: 'internal/ops/v2/review/compliance/verify-identity',
    loadComponent: () => import('./backOffice/verify-identity/back-office-verify-identity.component').then(m => m.BackOfficeVerifyIdentityComponent),
  },
  {
    path: 'internal/ops/v2/review/compliance/verify-2fa-recovery',
    loadComponent: () => import('./backOffice/verify-2fa-recovery/back-office-verify-2fa-recovery.component').then(m => m.BackOfficeVerify2faRecoveryComponent),
  },
  {
    path: 'internal/ops/v2/review/compliance/reset-password',
    loadComponent: () => import('./backOffice/reset-password/back-office-reset-password.component').then(m => m.BackOfficeResetPasswordComponent),
  },
  {
    path: 'internal/ops/v2/review/compliance/change-password',
    loadComponent: () => import('./backOffice/change-password/back-office-change-password.component').then(m => m.BackOfficeChangePasswordComponent),
  },
  {
    path: 'internal/ops/v2/review/compliance/setup-2fa',
    loadComponent: () => import('./backOffice/setup-2fa/back-office-setup-2fa.component').then(m => m.BackOfficeSetup2faComponent),
  },

  // Back Office — ruta ofuscada, acceso restringido con guard
  {
    path: 'internal/ops/v2/review/compliance',
    loadComponent: () => import('./backOffice/layout/back-office-layout.component').then(m => m.BackOfficeLayoutComponent),
    canActivate: [backOfficeAuthGuard],
    children: [
      {
        path: 'manage-requests',
        loadComponent: () => import('./backOffice/home/home.component').then(m => m.BackOfficeHomeComponent),
      },
      {
        path: 'detail/:type/:id',
        loadComponent: () => import('./backOffice/request-detail/request-detail.component').then(m => m.RequestDetailComponent),
      },
      { path: '', redirectTo: 'manage-requests', pathMatch: 'full' },
    ],
  },
];
